import { forwardRef, type Ref } from 'react';
import type { LucideIcon, LucideProps } from 'lucide-react';
import { cn } from '@/shared/lib/cn';

/**
 * Ícone Lucide com micro-animação — substitui emoji na UI.
 *
 * Animação em CSS puro (`fx-*` em shared/styles/index.css), NÃO framer-motion:
 * loop infinito do framer dentro de `AnimatePresence mode="wait"` (Wizard,
 * convite público) impede o passo anterior de sair, e o próximo nunca monta.
 * Keyframes CSS não entram no ciclo de presença e rodam no compositor.
 * `prefers-reduced-motion` é respeitado pela regra global do index.css.
 *
 * Em lista, use `loop={false}`: toca ao aparecer e no hover.
 */
export type IconMotion = 'pop' | 'wiggle' | 'float' | 'pulse' | 'flip' | 'bounce' | 'shake' | 'spin';

interface AnimatedIconProps extends Omit<LucideProps, 'ref'> {
  icon: LucideIcon;
  motion?: IconMotion;
  /** Repete para sempre (destaques). Em listas, deixe false. */
  loop?: boolean;
  /** Atraso em segundos (cascata). */
  delay?: number;
  /** Classe do wrapper animado (o `className` vai no ícone). */
  wrapperClassName?: string;
  iconRef?: Ref<SVGSVGElement>;
}

export function AnimatedIcon({
  icon: Icon,
  motion: kind = 'pop',
  loop = false,
  delay = 0,
  wrapperClassName,
  iconRef,
  ...iconProps
}: AnimatedIconProps) {
  return (
    <span
      aria-hidden
      className={cn(
        'fx-icon fx-hover shrink-0 items-center justify-center',
        `fx-${kind}${loop ? '-loop' : ''}`,
        wrapperClassName,
      )}
      style={delay ? { animationDelay: `${delay}s` } : undefined}
    >
      <Icon ref={iconRef} {...iconProps} />
    </span>
  );
}

/**
 * O mesmo, com a assinatura de um ícone Lucide — para componentes que recebem
 * `icon: LucideIcon` (BadgeTooltip, KpiCard, SegmentedControl…). Crie no
 * escopo do módulo (não dentro de render), senão remonta a cada render.
 */
// eslint-disable-next-line react-refresh/only-export-components -- fábrica de componente, mesmo padrão de shared/lib/passbook.tsx
export function animatedIcon(Icon: LucideIcon, kind: IconMotion, loop = false): LucideIcon {
  const Animated = forwardRef<SVGSVGElement, Omit<LucideProps, 'ref'>>(function Animated(props, ref) {
    return <AnimatedIcon icon={Icon} motion={kind} loop={loop} iconRef={ref} {...props} />;
  });
  Animated.displayName = `Animated(${Icon.displayName ?? 'Icon'})`;
  return Animated as LucideIcon;
}
